// Modal do nó OpenAI (casca Aeros): mesmos campos, validação Yup e payload
// `typebotIntegration` do modal original — só a apresentação mudou.
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Field, FieldArray } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Switch from "@material-ui/core/Switch";
import Radio from "@material-ui/core/Radio";
import { Visibility, VisibilityOff, Add, Close } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";

const T = (k, o) => i18n.t(`aeros.flow.modals.openai.${k}`, o);

// Lista de modelos OpenAI suportados
const openAiModels = [
  "gpt-3.5-turbo",
  "gpt-3.5-turbo-1106",
  "gpt-3.5-turbo-16k",
  "gpt-4",
  "gpt-4-turbo",
  "gpt-4o",
  "gpt-4o-mini",
];

// Lista de vozes disponíveis para OpenAI
const availableVoices = [
  "texto",
  "pt-BR-FabioNeural",
  "pt-BR-FranciscaNeural",
  "pt-BR-AntonioNeural",
  "pt-BR-BrendaNeural",
  "pt-BR-DonatoNeural",
  "pt-BR-ElzaNeural",
  "pt-BR-GiovannaNeural",
  "pt-BR-HumbertoNeural",
  "pt-BR-JulioNeural",
  "pt-BR-LeilaNeural",
  "pt-BR-LeticiaNeural",
  "pt-BR-ManuelaNeural",
  "pt-BR-NicolauNeural",
  "pt-BR-ValerioNeural",
  "pt-BR-YaraNeural",
];

const DEFAULT_KEYWORDS = ["continuar", "próximo", "avançar"];

export const useAiModalStyles = makeStyles((theme) => ({
  sectionBody: { padding: 14, display: "flex", flexDirection: "column", gap: 12 },
  grid3: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 10,
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  modeBox: { cursor: "pointer", "& .MuiRadio-root": { padding: 4, marginLeft: -4 } },
  modeOn: { borderColor: "rgba(160,68,255,0.5)" },
  keywords: { display: "flex", flexWrap: "wrap", gap: 6 },
  keywordChip: { backgroundColor: "rgba(34,211,238,0.12)", color: theme.palette.type === "light" ? "#0E7490" : "#22D3EE" },
  keywordRow: { display: "flex", gap: 8, alignItems: "center" },
  error: { fontSize: 11, color: "#F87171", marginTop: 4 },
}));

// Validação (mensagens resolvidas no idioma atual, por isso é função)
const buildSchema = () =>
  Yup.object().shape({
    name: Yup.string().min(5, T("errors.tooShort")).max(100, T("errors.tooLong")).required(T("errors.required")),
    prompt: Yup.string().min(50, T("errors.tooShort")).required(T("errors.promptRequired")),
    model: Yup.string().oneOf(openAiModels, T("errors.invalidModel")).required(T("errors.modelRequired")),
    maxTokens: Yup.number()
      .min(10, T("errors.minTokens", { n: 10 }))
      .max(4000, T("errors.maxTokens", { n: 4000 }))
      .required(T("errors.required")),
    temperature: Yup.number().min(0, T("errors.min", { n: 0 })).max(2, T("errors.max", { n: 2 })).required(T("errors.required")),
    apiKey: Yup.string().required(T("errors.apiKeyRequired")),
    maxMessages: Yup.number().min(1, T("errors.min", { n: 1 })).max(100, T("errors.max", { n: 100 })).required(T("errors.required")),
    voice: Yup.string().required(T("errors.required")),
    voiceKey: Yup.string().when("voice", {
      is: (voice) => voice !== "texto",
      then: Yup.string().required(T("errors.voiceKeyRequired")),
      otherwise: Yup.string().notRequired(),
    }),
    voiceRegion: Yup.string().when("voice", {
      is: (voice) => voice !== "texto",
      then: Yup.string().required(T("errors.voiceRegionRequired")),
      otherwise: Yup.string().notRequired(),
    }),
    flowMode: Yup.string().oneOf(["permanent", "temporary"]).required(T("errors.required")),
    maxInteractions: Yup.number().when("flowMode", {
      is: "temporary",
      then: Yup.number().min(1, T("errors.min", { n: 1 })).max(50, T("errors.max", { n: 50 })).nullable(),
      otherwise: Yup.number().nullable(),
    }),
    completionTimeout: Yup.number().when("flowMode", {
      is: "temporary",
      then: Yup.number().min(1, T("errors.min", { n: 1 })).max(60, T("errors.max", { n: 60 })).nullable(),
      otherwise: Yup.number().nullable(),
    }),
    continueKeywords: Yup.array().when("flowMode", {
      is: "temporary",
      then: Yup.array().of(Yup.string().required(T("errors.keywordEmpty"))).min(1, T("errors.keywordsMin")),
      otherwise: Yup.array(),
    }),
    objective: Yup.string().when(["flowMode", "autoCompleteOnObjective"], {
      is: (flowMode, autoComplete) => flowMode === "temporary" && autoComplete,
      then: Yup.string().required(T("errors.objectiveRequired")),
      otherwise: Yup.string(),
    }),
  });

const initialState = {
  name: "",
  prompt: "",
  model: "gpt-4o",
  voice: "texto",
  voiceKey: "",
  voiceRegion: "",
  maxTokens: 1000,
  temperature: 0.7,
  apiKey: "",
  maxMessages: 10,
  queueId: 0,
  // Campos para controle de fluxo
  flowMode: "permanent",
  maxInteractions: 5,
  completionTimeout: 10,
  continueKeywords: DEFAULT_KEYWORDS,
  objective: "",
  autoCompleteOnObjective: false,
};

const modelNames = {
  "gpt-3.5-turbo": "GPT 3.5 Turbo",
  "gpt-3.5-turbo-1106": "GPT 3.5 Turbo (1106)",
  "gpt-3.5-turbo-16k": "GPT 3.5 Turbo 16K",
  "gpt-4": "GPT 4",
  "gpt-4-turbo": "GPT 4 Turbo",
  "gpt-4o": "GPT 4o",
  "gpt-4o-mini": "GPT 4o Mini",
};

const getVoiceDisplayName = (voice) => (voice === "texto" ? T("voiceTextOnly") : voice.replace("pt-BR-", "").replace("Neural", ""));

const FlowBuilderOpenAIModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useAiModalStyles();
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();
  const [showApiKey, setShowApiKey] = useState(false);
  const [integration, setIntegration] = useState(initialState);
  const [newKeyword, setNewKeyword] = useState("");

  useEffect(() => {
    if (open === "edit") {
      const typebotIntegration = data?.data?.typebotIntegration || {};
      setIntegration({
        ...initialState,
        ...typebotIntegration,
        model: openAiModels.includes(typebotIntegration.model) ? typebotIntegration.model : "gpt-4o",
        flowMode: typebotIntegration.flowMode || "permanent",
        continueKeywords: typebotIntegration.continueKeywords || DEFAULT_KEYWORDS,
        maxInteractions: typebotIntegration.maxInteractions || 5,
        completionTimeout: typebotIntegration.completionTimeout || 10,
        objective: typebotIntegration.objective || "",
        autoCompleteOnObjective: typebotIntegration.autoCompleteOnObjective || false,
      });
    } else if (open === "create") {
      setIntegration(initialState);
    }
  }, [open, data]);

  const handleClose = () => {
    setNewKeyword("");
    close(null);
  };

  const handleSavePrompt = (values, { setSubmitting }) => {
    const promptData = {
      ...values,
      // Garantir que campos do modo temporário sejam nulos se modo for permanente
      maxInteractions: values.flowMode === "temporary" ? values.maxInteractions : null,
      completionTimeout: values.flowMode === "temporary" ? values.completionTimeout : null,
      continueKeywords: values.flowMode === "temporary" ? values.continueKeywords : [],
      objective: values.flowMode === "temporary" ? values.objective : "",
      autoCompleteOnObjective: values.flowMode === "temporary" ? values.autoCompleteOnObjective : false,
      // Forçar provider como openai
      provider: "openai",
    };

    if (open === "edit") {
      onUpdate({ ...data, data: { typebotIntegration: promptData } });
    } else if (open === "create") {
      promptData.projectName = promptData.name;
      onSave({ typebotIntegration: promptData });
    }
    handleClose();
    setSubmitting(false);
  };

  const addKeyword = (arrayHelpers, values, keyword) => {
    if (keyword.trim() && !values.continueKeywords.includes(keyword.trim())) {
      arrayHelpers.push(keyword.trim());
      setNewKeyword("");
    }
  };

  // Campo padrão: rótulo acima + TextField outlined `m.input`, erro/dica abaixo.
  const field = (form, name, labelKey, hintKey, props = {}) => {
    const err = form.touched[name] && form.errors[name];
    return (
      <div>
        <label className={m.fieldLabel}>{T(labelKey)}</label>
        <Field as={TextField} name={name} variant="outlined" className={m.input} error={Boolean(err)} {...props} />
        {err ? <div className={classes.error}>{err}</div> : hintKey ? <div className={m.hint}>{T(hintKey)}</div> : null}
      </div>
    );
  };

  const modeBox = (form, value, titleKey, descKey) => (
    <div
      className={`${m.optionBox} ${classes.modeBox} ${form.values.flowMode === value ? classes.modeOn : ""}`}
      onClick={() => form.setFieldValue("flowMode", value)}
    >
      <Radio color="primary" checked={form.values.flowMode === value} value={value} name="flowMode" size="small" />
      <div>
        <div className={m.optionTitle}>{T(titleKey)}</div>
        <div className={m.optionDesc}>{T(descKey)}</div>
      </div>
    </div>
  );

  return (
    <Formik initialValues={integration} enableReinitialize validationSchema={buildSchema} onSubmit={handleSavePrompt}>
      {(form) => (
        <AerosDialog
          open={open === "create" || open === "edit"}
          onClose={handleClose}
          width={640}
          title={T(open === "edit" ? "editTitle" : "title")}
          subtitle={T("subtitle")}
          onSubmit={form.submitForm}
          submitLabel={i18n.t(open === "edit" ? "aeros.flow.save" : "aeros.flow.add")}
          loading={form.isSubmitting}
        >
          {/* CONFIGURAÇÕES BÁSICAS */}
          <div className={m.section}>
            <div className={m.sectionHead}>{T("basic")}</div>
            <div className={classes.sectionBody}>
              {field(form, "name", "name", null, { required: true })}
              {field(form, "apiKey", "apiKey", null, {
                type: showApiKey ? "text" : "password",
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowApiKey(!showApiKey)}>
                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              })}
              {field(form, "model", "model", null, {
                select: true,
                children: openAiModels.map((model) => (
                  <MenuItem key={model} value={model}>
                    {modelNames[model] || model}
                  </MenuItem>
                )),
              })}
              {field(form, "prompt", "prompt", null, { multiline: true, minRows: 6, placeholder: T("promptPlaceholder") })}
              <div className={classes.grid3}>
                {field(form, "temperature", "temperature", "temperatureHint", {
                  type: "number",
                  inputProps: { step: "0.1", min: "0", max: "2" },
                })}
                {field(form, "maxTokens", "maxTokens", "maxTokensHint", { type: "number" })}
                {field(form, "maxMessages", "maxMessages", "maxMessagesHint", { type: "number" })}
              </div>
            </div>
          </div>

          {/* CONFIGURAÇÕES DE VOZ */}
          <div className={m.section}>
            <div className={m.sectionHead}>{T("voiceSection")}</div>
            <div className={classes.sectionBody}>
              {field(form, "voice", "voice", null, {
                select: true,
                children: availableVoices.map((voice) => (
                  <MenuItem key={voice} value={voice}>
                    {getVoiceDisplayName(voice)}
                  </MenuItem>
                )),
              })}
              {form.values.voice !== "texto" && (
                <div className={m.grid2}>
                  {field(form, "voiceKey", "voiceKey", null, { placeholder: T("voiceKeyPlaceholder") })}
                  {field(form, "voiceRegion", "voiceRegion", null, { placeholder: "ex: brazilsouth" })}
                </div>
              )}
            </div>
          </div>

          {/* COMPORTAMENTO DO FLUXO */}
          <div className={m.section}>
            <div className={m.sectionHead}>{T("behavior")}</div>
            <div className={classes.sectionBody}>
              <div className={m.grid2}>
                {modeBox(form, "permanent", "permanent", "permanentDesc")}
                {modeBox(form, "temporary", "temporary", "temporaryDesc")}
              </div>

              {form.values.flowMode === "temporary" && (
                <>
                  <div className={m.hint}>{T("temporaryHint")}</div>
                  <div className={m.grid2}>
                    {field(form, "maxInteractions", "maxInteractions", "maxInteractionsHint", {
                      type: "number",
                      inputProps: { min: 0, max: 50 },
                    })}
                    {field(form, "completionTimeout", "completionTimeout", "completionTimeoutHint", {
                      type: "number",
                      inputProps: { min: 0, max: 60 },
                    })}
                  </div>

                  <FieldArray name="continueKeywords">
                    {(arrayHelpers) => (
                      <div>
                        <label className={m.fieldLabel}>{T("keywords")}</label>
                        <div className={classes.keywordRow}>
                          <TextField
                            variant="outlined"
                            className={`${m.input} ${m.inputSmall}`}
                            placeholder={T("keywordPlaceholder")}
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addKeyword(arrayHelpers, form.values, newKeyword);
                              }
                            }}
                          />
                          <button
                            type="button"
                            className={m.linkButton}
                            style={{ marginLeft: 0, whiteSpace: "nowrap" }}
                            onClick={() => addKeyword(arrayHelpers, form.values, newKeyword)}
                            disabled={!newKeyword.trim()}
                          >
                            <Add /> {i18n.t("aeros.flow.add")}
                          </button>
                        </div>
                        <div className={classes.keywords} style={{ marginTop: 8 }}>
                          {form.values.continueKeywords.map((keyword, index) => (
                            <span key={index} className={`${m.chip} ${classes.keywordChip}`}>
                              {keyword}
                              <Close onClick={() => arrayHelpers.remove(index)} />
                            </span>
                          ))}
                        </div>
                        <div className={m.hint}>{T("keywordsHint")}</div>
                        {form.touched.continueKeywords && typeof form.errors.continueKeywords === "string" && (
                          <div className={classes.error}>{form.errors.continueKeywords}</div>
                        )}
                      </div>
                    )}
                  </FieldArray>

                  {field(form, "objective", "objective", "objectiveHint", {
                    multiline: true,
                    minRows: 2,
                    placeholder: T("objectivePlaceholder"),
                  })}

                  <div className={m.optionBox}>
                    <div style={{ flex: 1 }}>
                      <div className={m.optionTitle}>{T("autoComplete")}</div>
                      <div className={m.optionDesc}>{T("autoCompleteDesc")}</div>
                    </div>
                    <Switch
                      color="primary"
                      className={aeros.toggleSwitchSmall}
                      checked={form.values.autoCompleteOnObjective}
                      onChange={(e) => form.setFieldValue("autoCompleteOnObjective", e.target.checked)}
                      name="autoCompleteOnObjective"
                    />
                  </div>

                  {field(form, "queueId", "queueId", "queueIdHint", { type: "number", inputProps: { min: 0 } })}
                </>
              )}
            </div>
          </div>
        </AerosDialog>
      )}
    </Formik>
  );
};

export default FlowBuilderOpenAIModal;
